import { ServiceException } from '@inter-ikea-kompis/exceptions';
import { GlobalCacheStore } from '@inter-ikea-kompis/utilities';
import CacheStoreType from '../../enums/CacheStoreType.js';
import DexfWebplannerService from '../../services/product/DexfWebplannerService.js';
import DexfConfiguration from '../../utilities/DexfConfiguration.js';
import ProductCache from './ProductCache.js';
import ProductItemQueue from './ProductItemQueue.js';
/**
 * This class is used as a simple interface for product services.
 */
export default class ProductService {
    /**
     * Constructor.
     *
     * @param options Options.
     */
    constructor(options) {
        this.locale = options.locale;
        this.dexfApplicationId = options.dexfApplicationId || null;
        this.cache = options.cache || new GlobalCacheStore();
        this.cacheStore = this.cache.getStore(CacheStoreType.product);
        this.productCache = new ProductCache(options.locale, this.cache);
        this.dexfConfiguration = new DexfConfiguration(options);
    }
    /**
     * Fetches and returns products.
     *
     * @param items Array of items.
     * @param fields Fields.
     * @param params Optional params.
     * @returns Promise.
     */
    async getProducts(items, fields, params) {
        const cached = this.productCache.getProducts(items, fields);
        if (cached.missing.length === 0) {
            return cached.products;
        }
        const productItemQueueKey = 'queue-' + this.locale + '-' + fields.sort().join('_');
        let productItemQueue = this.cacheStore.get(productItemQueueKey);
        if (!productItemQueue) {
            productItemQueue = new ProductItemQueue();
            this.cacheStore.set(productItemQueueKey, productItemQueue);
        }
        // eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
        const callbacks = [];
        let itemsAndCallbacks = { items, callbacks };
        itemsAndCallbacks = await productItemQueue.retrieveItems(cached.missing);
        if (itemsAndCallbacks.items.length > 0) {
            const service = await this.getService();
            const products = await service.getProducts(itemsAndCallbacks.items, fields, params);
            this.productCache.addProducts(products, fields);
            productItemQueue.releaseWaiting(itemsAndCallbacks.callbacks);
        }
        return this.productCache.getProducts(items, fields).products;
    }
    /**
     * Returns an Array of fetched ShoppingProduct objects.
     *
     * @param shoppingItems Array of ShoppingItem.
     * @param fields Fields.
     * @returns Array of ShoppingProduct objects.
     */
    async getShoppingProducts(shoppingItems, fields) {
        const products = await this.getProducts(shoppingItems, fields);
        const shoppingItemsMap = {};
        for (const shoppingItem of shoppingItems) {
            const type = shoppingItem.type.toLowerCase();
            shoppingItemsMap[type + '-' + shoppingItem.id] = shoppingItem;
        }
        return products.map((product) => {
            if (!product.content) {
                return {
                    product,
                    quantity: shoppingItemsMap[product.itemId.toLowerCase()].quantity
                };
            }
            const itemType = product.content.itemType?.toLowerCase();
            const item = shoppingItemsMap[itemType + '-' + product.content.itemNoLocal] ||
                shoppingItemsMap[itemType + '-' + product.content.itemNoGlobal];
            return {
                product,
                quantity: item.quantity
            };
        });
    }
    /**
     * Fetches and returns all products for a planner.
     *
     * @param fields Fields.
     * @param params Optional params.
     * @returns Promise.
     */
    async getApplicationProducts(fields, params) {
        if (!this.dexfApplicationId) {
            throw new ServiceException('Failed to fetch application products. The configuration "dexfApplicationId" has not been defined. Send it in to the constructor of ProductService.');
        }
        const service = await this.getService();
        const products = await service.getApplicationProducts(this.dexfApplicationId, fields, params);
        this.productCache.addProducts(products, fields);
        return products;
    }
    /**
     * Returns service.
     *
     * @returns Service.
     */
    async getService() {
        const { kompis } = await this.dexfConfiguration.getSettings();
        const dexfApiKey = await this.dexfConfiguration.getDexfApiKeyOrThrow();
        return new DexfWebplannerService({
            endpoint: kompis.endpoints.dexfWebplannerService,
            dexfApiKey
        });
    }
}
//# sourceMappingURL=ProductService.js.map